import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IScheduleSummary } from '@interfaces/IScheduler';
import type { ScheduleImportFormat } from '@apis/schedule';
import { Menu } from '@components/ui/Menu/Menu';
import primaveraLogo from '@assets/scheduleImport/primavera.png';
import msprojectLogo from '@assets/scheduleImport/msproject.png';
import styles from './ScheduleMenu.module.css';

interface IImportOption {
  format: ScheduleImportFormat;
  accept: string;
  logo: string;
  vendorKey: string;
  formatKey: string;
}

// One row per source format, none privileged — the platform-neutrality story made
// visible. Both Primavera formats carry the Primavera mark; MS Project its own.
const IMPORT_OPTIONS: IImportOption[] = [
  { format: 'primavera-xer', accept: '.xer', logo: primaveraLogo, vendorKey: 'scheduler.import.vendor.primavera', formatKey: 'scheduler.import.format.xer' },
  { format: 'primavera-xml', accept: '.xml', logo: primaveraLogo, vendorKey: 'scheduler.import.vendor.primavera', formatKey: 'scheduler.import.format.p6xml' },
  { format: 'msproject-xml', accept: '.xml', logo: msprojectLogo, vendorKey: 'scheduler.import.vendor.msproject', formatKey: 'scheduler.import.format.mspxml' },
];

export interface IScheduleMenuProps {
  schedules: IScheduleSummary[];
  /** Currently selected scheduleNumber. */
  selectedScheduleNumber?: string;
  /** Optional — omit when there are no schedules to select (import-only state). */
  onSelect?: (scheduleNumber: string) => void;
  /** Import a chosen file, with the format pinned by the clicked menu item.
   *  Omit for a user who may not import — no import section is rendered. */
  onImportFile?: (file: File, format: ScheduleImportFormat) => void;
  /** A file import is in flight. */
  importing?: boolean;
  /**
   * Trigger chrome. `default` is the standalone bordered pill (the empty-state
   * import CTA). `breadcrumb` is borderless to match the project selector when
   * the picker sits in the TopBar breadcrumb.
   */
  triggerVariant?: 'default' | 'breadcrumb';
  /** Portal target for the dropdown. Omit for the default (document.body); the
   *  Scheduler passes its fullscreen element so the menu stays inside it. Keeps
   *  this shared component free of any Scheduler-specific context. */
  portalContainer?: HTMLElement | null;
  /** Create a new (empty) schedule. When provided, a "create new" item is
   *  appended after the import section (opens the create-name modal). */
  onCreateNew?: () => void;
}

/**
 * The schedule selector — a Base UI menu (not a native select) that lists the
 * site's schedules AND, in the same surface, the upload-a-file import options.
 * Each import row shows its source vendor's mark so the "we read any major
 * format" story is legible at a glance.
 */
export function ScheduleMenu({
  schedules,
  selectedScheduleNumber,
  onSelect,
  onImportFile,
  importing,
  triggerVariant = 'default',
  portalContainer,
  onCreateNew,
}: IScheduleMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingFormat = useRef<ScheduleImportFormat | null>(null);

  const selected = schedules.find((s) => s.scheduleNumber === selectedScheduleNumber);
  const triggerLabel = selected
    ? selected.scheduleName || selected.scheduleNumber
    : t('scheduler.picker.placeholder');

  const pick = (format: ScheduleImportFormat, accept: string) => {
    pendingFormat.current = format;
    const input = inputRef.current;
    if (!input) return;
    input.accept = accept;
    input.click();
  };

  return (
    <>
      <Menu.Root open={open} onOpenChange={setOpen}>
        <Menu.Trigger
          render={
            <button
              type="button"
              className={`${styles.trigger} ${triggerVariant === 'breadcrumb' ? styles.triggerBreadcrumb : ''}`}
              data-testid="schedule-picker"
            />
          }
        >
          <CalendarGlyph />
          <span className={styles.triggerLabel}>{triggerLabel}</span>
          <ChevronGlyph />
        </Menu.Trigger>
        <Menu.Portal container={portalContainer ?? undefined}>
          <Menu.Positioner side="bottom" align="start" sideOffset={4}>
            <Menu.Popup className={styles.popup}>
              {schedules.length > 0 && (
                <>
                  <div className={styles.sectionLabel}>{t('scheduler.picker.label')}</div>
                  {schedules.map((schedule) => {
                    const label = schedule.scheduleName || schedule.scheduleNumber;
                    const isSelected = schedule.scheduleNumber === selectedScheduleNumber;
                    return (
                      <Menu.Item
                        key={schedule.scheduleNumber}
                        onClick={() => onSelect?.(schedule.scheduleNumber)}
                      >
                        <span className={styles.check} aria-hidden>{isSelected ? <CheckGlyph /> : null}</span>
                        <span className={styles.scheduleName}>
                          {schedule.imported ? label : `${t('scheduler.import.notImportedTag')} · ${label}`}
                        </span>
                      </Menu.Item>
                    );
                  })}
                  {onImportFile && <Menu.Separator />}
                </>
              )}

              {onImportFile && (
                <>
                  <div className={styles.sectionLabel}>{t('scheduler.import.fromFile')}</div>
                  {IMPORT_OPTIONS.map((opt) => (
                    <Menu.Item
                      key={opt.format}
                      disabled={importing}
                      onClick={() => pick(opt.format, opt.accept)}
                    >
                      <img src={opt.logo} alt="" className={styles.logo} width={28} height={28} />
                      <span className={styles.importText}>
                        <span className={styles.vendor}>{t(opt.vendorKey)}</span>
                        <span className={styles.format}>{t(opt.formatKey)}</span>
                      </span>
                    </Menu.Item>
                  ))}
                </>
              )}

              {onCreateNew && (
                <>
                  {(onImportFile || schedules.length > 0) && <Menu.Separator />}
                  <Menu.Item onClick={onCreateNew} data-testid="schedule-create-new">
                    <span className={styles.createGlyph} aria-hidden><PlusGlyph /></span>
                    <span className={styles.createLabel}>{t('scheduler.create.menuItem')}</span>
                  </Menu.Item>
                </>
              )}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>

      {onImportFile && (
        <input
          ref={inputRef}
          type="file"
          className={styles.hiddenInput}
          data-testid="schedule-import-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            const format = pendingFormat.current;
            if (file && format) onImportFile(file, format);
            pendingFormat.current = null;
            e.target.value = '';
          }}
        />
      )}
    </>
  );
}

function CalendarGlyph() {
  return (
    <svg className={styles.icon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </svg>
  );
}

function ChevronGlyph() {
  return (
    <svg className={styles.chevron} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function PlusGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}
